import type { FC, ReactNode } from 'react'
import type { City, GiftPackage } from '@/types'
import type { OrderInput } from '@/lib/order-schema'
import type { PackageOption } from '@/components/controls/package-radio'

import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Loader2, Send } from 'lucide-react'
import { formatPrice } from '@/lib/format'
import { PAYMENT_METHODS, createOrderSchema } from '@/lib/order-schema'
import { useText } from '@/lib/site-content'
import Button from '@/components/controls/button'
import Checkbox from '@/components/controls/checkbox'
import Field from '@/components/controls/field'
import PackageRadio from '@/components/controls/package-radio'
import Select from '@/components/controls/select'
import Textarea from '@/components/controls/textarea'
import CmsText from '@/components/generals/cms-text'

interface OrderFormProps {
    packages: GiftPackage[]
    cities: City[]
}

const OrderForm: FC<OrderFormProps> = ({ packages, cities }): ReactNode => {
    const t = useText()
    const [submitting, setSubmitting] = useState(false)

    const packageCodes = useMemo(() => packages.map(pkg => pkg.code), [packages])
    const schema = useMemo(() => createOrderSchema({ packageCodes, text: t }), [packageCodes, t])

    const emptyOrder = useMemo<OrderInput>(() => ({
        pako: packageCodes[0] ?? '',
        emri: '',
        email: '',
        tel: '',
        tel2: '',
        sasia: undefined as unknown as number,
        porosiShtese: false,
        pagesa: PAYMENT_METHODS[0],
        qyteti: '',
        adresa: '',
        mesazhi: '',
        hp_subject: ''
    }), [packageCodes])

    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<OrderInput>({
        resolver: zodResolver(schema),
        defaultValues: emptyOrder,
        mode: 'onBlur'
    })

    const onSubmit = handleSubmit(async values => {
        setSubmitting(true)

        try {
            const res = await fetch('/api/order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await res.json().catch(() => ({})) as { ok?: boolean, error?: string }

            if (!res.ok || !data.ok) throw new Error(data.error ?? 'request_failed')

            toast.success(t('order.successTitle'), { description: t('order.successDesc') })
            reset()
        } catch {
            toast.error(t('order.errorTitle'), { description: t('order.errorDesc') })
        } finally {
            setSubmitting(false)
        }
    })

    const packageOptions: PackageOption[] = packages.map(pkg => ({
        value: pkg.code,
        label: pkg.label,
        labelCms: `packages:${pkg.id}:label`,
        helper: pkg.tagline,
        helperCms: `packages:${pkg.id}:tagline`,
        trailing: formatPrice(pkg.price, pkg.currency)
    }))

    return (
        <motion.form
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className='space-y-5'
        >
            {/* Honeypot: visually hidden but reachable for bots */}
            <div className='hidden' aria-hidden>
                <label>
                    Subject
                    <input type='text' tabIndex={-1} autoComplete='off' {...register('hp_subject')} />
                </label>
            </div>

            <Controller
                control={control}
                name='pako'
                render={({ field }) => (
                    <PackageRadio
                        legend={<CmsText textKey='order.fields.pako.legend' />}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        options={packageOptions}
                        error={errors.pako?.message}
                    />
                )}
            />

            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <Field
                    {...register('emri')}
                    placeholder={t('order.fields.emri.placeholder')}
                    autoComplete='name'
                    error={errors.emri?.message}
                />

                <Field
                    {...register('email')}
                    type='email'
                    placeholder={t('order.fields.email.placeholder')}
                    autoComplete='email'
                    error={errors.email?.message}
                />

                <Field
                    {...register('tel')}
                    type='tel'
                    placeholder={t('order.fields.tel.placeholder')}
                    autoComplete='tel'
                    error={errors.tel?.message}
                />

                <Field
                    {...register('tel2')}
                    type='tel'
                    placeholder={t('order.fields.tel2.placeholder')}
                    autoComplete='tel'
                    error={errors.tel2?.message}
                />

                <Field
                    {...register('sasia')}
                    type='number'
                    inputMode='numeric'
                    min={1}
                    max={999}
                    placeholder={t('order.fields.sasia.placeholder')}
                    error={errors.sasia?.message}
                />

                <div className='flex items-end'>
                    <Checkbox
                        {...register('porosiShtese')}
                        label={<CmsText textKey='order.fields.porosiShtese.label' />}
                    />
                </div>
            </div>

            <Select
                {...register('pagesa')}
                label={<CmsText textKey='order.fields.pagesa.label' />}
                error={errors.pagesa?.message}
            >
                <CmsText as='option' textKey='order.fields.pagesa.options.postier' value={PAYMENT_METHODS[0]} />
                <CmsText as='option' textKey='order.fields.pagesa.options.banke' value={PAYMENT_METHODS[1]} />
            </Select>

            <Select
                {...register('qyteti')}
                label={<CmsText textKey='order.fields.qyteti.label' />}
                error={errors.qyteti?.message}
            >
                <CmsText as='option' textKey='order.fields.qyteti.placeholder' value='' />

                {cities.map(city => (
                    <option key={city.id} value={city.name} data-mdlfy-cms={`cities:${city.id}:name`}>
                        {city.name}
                    </option>
                ))}
            </Select>

            <Field
                {...register('adresa')}
                placeholder={t('order.fields.adresa.placeholder')}
                autoComplete='street-address'
                error={errors.adresa?.message}
            />

            <Textarea
                {...register('mesazhi')}
                placeholder={t('order.fields.mesazhi.placeholder')}
                rows={5}
                error={errors.mesazhi?.message}
            />

            <Button type='submit' disabled={submitting} variant='yellow' size='lg' className='mt-2'>
                {submitting ? (
                    <>
                        <Loader2 aria-hidden className='h-4 w-4 animate-spin' />
                        <CmsText textKey='order.submitting' />
                    </>
                ) : (
                    <>
                        <Send aria-hidden className='h-4 w-4' />
                        <CmsText textKey='order.submit' />
                    </>
                )}
            </Button>
        </motion.form>
    )
}

export default OrderForm
